import React, {useEffect, useMemo, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {useMutation} from "@tanstack/react-query";
import {BackButton} from "components/BackButton";
import {Layout} from "components/Layout";
import {useFetchTargetGroupsLvl2} from "hooks/useQuery/useFetchTargetGroupsLvl2";
import {createTargetGroup} from "api/targetGroups/createTargetGroup";
import {updateTargetGroup} from "api/targetGroups/updateTargetGroup";
import {deleteTargetGroup} from "api/targetGroups/deleteTargetGroup";
import {getSchools} from "redux/actions/setSchoolActions";

export const RiskGroupsLvl2 = () => {
    const { parentGroupId } = useParams();
    const history = useHistory();
    const dispatch = useDispatch();
    const { schools } = useSelector((state) => state.school);

    const [selectedSchoolId, setSelectedSchoolId] = useState("");
    const [isCreating, setIsCreating] = useState(false);
    const [newGroupName, setNewGroupName] = useState("");
    const [editingGroupId, setEditingGroupId] = useState(null);
    const [editingGroupName, setEditingGroupName] = useState("");

    useEffect(() => {
        if (!schools?.length) {
            dispatch(getSchools());
        }
    }, [dispatch, schools]);

    const effectiveSchoolId = useMemo(() => {
        if (selectedSchoolId) {
            return selectedSchoolId;
        }

        if (schools?.length === 1) {
            return schools[0]._id;
        }

        return "";
    }, [schools, selectedSchoolId]);

    const canLoadGroups = Boolean(parentGroupId && effectiveSchoolId);
    const { targetGroupsLvl2, targetGroupsLvl2IsLoading, refetchTargetGroupsLvl2 } = useFetchTargetGroupsLvl2(
        parentGroupId,
        effectiveSchoolId,
        canLoadGroups,
    );

    const createMutation = useMutation({
        mutationFn: (data) => createTargetGroup(2, data),
        onSuccess: () => {
            refetchTargetGroupsLvl2();
            setIsCreating(false);
            setNewGroupName("");
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, name }) => updateTargetGroup(id, { name }),
        onSuccess: () => {
            refetchTargetGroupsLvl2();
            setEditingGroupId(null);
            setEditingGroupName("");
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => deleteTargetGroup(id),
        onSuccess: () => {
            refetchTargetGroupsLvl2();
        },
    });

    const handleCreate = () => {
        if (newGroupName.trim() && effectiveSchoolId) {
            createMutation.mutate({
                name: newGroupName,
                parentGroup: parentGroupId,
                schoolId: effectiveSchoolId,
            });
        }
    };

    const handleUpdate = (id) => {
        if (editingGroupName.trim()) {
            updateMutation.mutate({ id, name: editingGroupName });
        }
    };

    const handleDelete = (id) => {
        if (window.confirm("Вы уверены, что хотите удалить эту группу?")) {
            deleteMutation.mutate(id);
        }
    };

    const startEdit = (group) => {
        setEditingGroupId(group._id);
        setEditingGroupName(group.name);
    };

    const cancelEdit = () => {
        setEditingGroupId(null);
        setEditingGroupName("");
    };

    const openGroup = (groupId) => {
        history.push(`/riskGroups/lvl2/${parentGroupId}/group/${groupId}?schoolId=${effectiveSchoolId}`);
    };

    return (
        <Layout>
            <div className="page">
                <header className="page__header">
                    <BackButton />
                    <p className="page__title">Целевые группы второго уровня</p>
                </header>

                <div className="school-page__wrapper">
                    {schools?.length > 1 && (
                        <div style={{ marginBottom: "1rem" }}>
                            <label htmlFor="risk-groups-school" style={{ display: "block", marginBottom: "0.5rem" }}>
                                Школа
                            </label>
                            <select
                                id="risk-groups-school"
                                value={selectedSchoolId}
                                onChange={(event) => setSelectedSchoolId(event.target.value)}
                                style={{ width: "100%", padding: "0.75rem", borderRadius: "8px", border: "1px solid #ddd" }}
                            >
                                <option value="">Выберите школу</option>
                                {schools.map((school) => (
                                    <option key={school._id} value={school._id}>
                                        {school.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {!effectiveSchoolId && schools?.length > 1 ? (
                        <p style={{ textAlign: "center", padding: "2rem", color: "#888" }}>
                            Сначала выберите школу, чтобы открыть целевые группы второго уровня
                        </p>
                    ) : (
                        <>
                            <div style={{ marginBottom: "1rem" }}>
                                {!isCreating ? (
                                    <button
                                        onClick={() => setIsCreating(true)}
                                        className="button button--primary"
                                        style={{ marginBottom: "1rem" }}
                                    >
                                        + Создать группу
                                    </button>
                                ) : (
                                    <div
                                        style={{
                                            marginBottom: "1rem",
                                            padding: "1rem",
                                            border: "1px solid #ddd",
                                            borderRadius: "8px",
                                        }}
                                    >
                                        <input
                                            type="text"
                                            value={newGroupName}
                                            onChange={(event) => setNewGroupName(event.target.value)}
                                            placeholder="Название группы"
                                            style={{ width: "100%", padding: "0.5rem", marginBottom: "0.5rem" }}
                                        />
                                        <div style={{ display: "flex", gap: "0.5rem" }}>
                                            <button
                                                onClick={handleCreate}
                                                disabled={createMutation.isPending || !effectiveSchoolId}
                                                className="button button--primary"
                                            >
                                                {createMutation.isPending ? "Создание..." : "Создать"}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setIsCreating(false);
                                                    setNewGroupName("");
                                                }}
                                                className="button button--secondary"
                                            >
                                                Отмена
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {targetGroupsLvl2IsLoading ? (
                                <p style={{ textAlign: "center", padding: "2rem" }}>Загрузка...</p>
                            ) : targetGroupsLvl2.length > 0 ? (
                                <div className="list gesturedZone risk-groups__list">
                                    {targetGroupsLvl2.map((group) => (
                                        <div key={group._id} className="risk-groups__list-item">
                                            {editingGroupId === group._id ? (
                                                <div style={{ width: "100%" }}>
                                                    <input
                                                        type="text"
                                                        value={editingGroupName}
                                                        onChange={(event) => setEditingGroupName(event.target.value)}
                                                        style={{ width: "100%", padding: "0.5rem", marginBottom: "0.5rem" }}
                                                    />
                                                    <div style={{ display: "flex", gap: "0.5rem" }}>
                                                        <button
                                                            onClick={() => handleUpdate(group._id)}
                                                            disabled={updateMutation.isPending}
                                                            className="button button--primary"
                                                            style={{ fontSize: "0.875rem", padding: "0.25rem 0.5rem" }}
                                                        >
                                                            Сохранить
                                                        </button>
                                                        <button
                                                            onClick={cancelEdit}
                                                            className="button button--secondary"
                                                            style={{ fontSize: "0.875rem", padding: "0.25rem 0.5rem" }}
                                                        >
                                                            Отмена
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div onClick={() => openGroup(group._id)} style={{ flex: 1, cursor: "pointer" }}>
                                                        <span className="risk-groups__tag">Целевая группа</span>
                                                        <p>{group.name}</p>
                                                    </div>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: "1rem",
                                                            flexDirection: "column-reverse",
                                                        }}
                                                    >
                                                        <p className="risk-groups__school">
                                                            <span>Учеников: {group.pupilsCount || 0}</span>
                                                        </p>
                                                        <div style={{ display: "flex", gap: "0.5rem" }}>
                                                            <button
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    startEdit(group);
                                                                }}
                                                                className="button button--secondary"
                                                                style={{ fontSize: "0.875rem", padding: "0.25rem 0.5rem" }}
                                                            >
                                                                ✏️
                                                            </button>
                                                            <button
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    handleDelete(group._id);
                                                                }}
                                                                disabled={deleteMutation.isPending}
                                                                className="button button--danger"
                                                                style={{ fontSize: "0.875rem", padding: "0.25rem 0.5rem" }}
                                                            >
                                                                🗑️
                                                            </button>
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p style={{ textAlign: "center", padding: "2rem", color: "#888" }}>
                                    Целевые группы не найдены
                                </p>
                            )}
                        </>
                    )}
                </div>
            </div>
        </Layout>
    );
};
